import Immutable from 'immutable';

export enum ValueModificationType {
    Delete = 0,
    Modify = 1,
    Add = 2
}

function applyDeltaCtx(delta, obj, version, ctx) {
    if (Array.isArray(delta)) {
        return [ctx, applyValueDelta(delta)];
    } else if (isObject(delta)) {
        if (delta['_t'] === 'a') {
            return [ctx, applyArray(delta, obj, version)];
        } else if (delta['_t'] === 's') {
            return [ctx, applySet(delta, obj)];
        } else if (delta['_t'] === 'i') {
            return applyIdentityArray(delta, obj, version, ctx);
        } else {
            return [ctx, applyObjectDiff(delta, obj, version)];
        }
    }
    throw new Error('Invalid delta: ' + delta + ' for object: ' + obj);
}
export function applyDelta(delta, obj?, version?) {
    const [[, ctx], value] = applyDeltaCtx(delta, obj, version, [null, Immutable.Map()]);
    if (!isUndefined(version) && ctx.size > 0) {
        throw new Error('Top level object must be object');
    }
    return value;
}

function isObject(value) {
    return typeof value === 'object';
}

function isUndefined(value) {
    return typeof value === 'undefined';
}

function checkAndUnwrapPrimitives(value: unknown) {
    if (value instanceof Number || value instanceof Boolean || value instanceof String) {
        if (value[Symbol.iterator]) {
            console.error(
                `Prevented possible memory leak. Primitive wrapper class with [Symbol.iterator] can break Immutable.fromJS`,
                value
            );
        } else {
            console.warn(`Primitive wrapper class should be avoided`, value);
        }
        return value.valueOf();
    }
    return value;
}

function applyValueDelta(delta) {
    const [changeCode, newValue] = delta;

    switch (changeCode) {
        case ValueModificationType.Delete:
            return undefined;
        case ValueModificationType.Modify:
            return Immutable.fromJS(checkAndUnwrapPrimitives(newValue));
        case ValueModificationType.Add:
            return Immutable.fromJS(checkAndUnwrapPrimitives(newValue));
    }
    throw new Error('Invalid value delta ' + delta);
}

function isPrimitive(value) {
    const type = typeof value;
    return type === 'string' || type === 'number' || type === 'boolean';
}

function canApplyDelta(obj, key, version) {
    if (typeof version !== 'undefined') {
        const valueVersion = obj.getIn(['__meta', 'version', key], -1);
        const deleteVersion = obj.getIn(['__meta', 'delete', key], -1);
        return valueVersion < version && deleteVersion < version;
    }
    return true;
}

function markValueVersion(obj, key, oldValue, version) {
    const value = obj.get(key);
    if (typeof version !== 'undefined') {
        if (typeof value === 'undefined') {
            return obj
                .updateIn(['__meta', 'delete', key], -1, oldVersion => Math.max(oldVersion, version))
                .removeIn(['__meta', 'version', key]);
        } else if (isPrimitive(value) || isPrimitive(oldValue)) {
            return obj.setIn(['__meta', 'version', key], version);
        }
    }
    return obj;
}

function removeOldValues(obj, version) {
    if (typeof version !== 'undefined') {
        if (isPrimitive(obj)) {
            return undefined;
        } else if (Immutable.Map.isMap(obj)) {
            obj = obj
                .map((v, k) => {
                    if (k !== '__meta' && canApplyDelta(obj, k, version)) {
                        return removeOldValues(v, version);
                    }
                    return v;
                })
                .filter(v => typeof v !== 'undefined');
            return obj.size > 1 ? obj : undefined;
        } else if (Immutable.isImmutable(obj)) {
            obj = obj.map(v => removeOldValues(v, version)).filter(v => typeof v !== 'undefined');
            return obj.size > 0 ? obj : undefined;
        }
    }
    return undefined;
}

function deleteValue(obj, key, version) {
    if (typeof version === 'undefined') {
        return obj.delete(key);
    }

    if (canApplyDelta(obj, key, version)) {
        if (isPrimitive(obj.get(key))) {
            return obj.delete(key);
        } else if (Immutable.isImmutable(obj.get(key))) {
            const value = removeOldValues(obj.get(key), version);
            if (typeof value !== 'undefined') {
                return obj.set(key, value);
            } else {
                return obj.delete(key);
            }
        }
    }
    return obj;
}

function applyObjectDiff(delta, obj, version) {
    return Object.keys(delta).reduce((obj, key) => {
        if (canApplyDelta(obj, key, version)) {
            const oldValue = obj.get(key);
            const [[, meta], value] = applyDeltaCtx(delta[key], oldValue, version, [key, obj.get('__meta')]);

            if (!isUndefined(meta)) {
                obj = obj.set('__meta', meta);
            }

            if (!isUndefined(value)) {
                obj = obj.set(key, value);
            } else {
                obj = deleteValue(obj, key, version);
            }
            obj = markValueVersion(obj, key, oldValue, version);
            return obj;
        }
        return obj;
    }, obj || Immutable.Map());
}

function isSourceIndex(index) {
    return index.startsWith('_');
}

function parseIndex(index) {
    if (isSourceIndex(index)) {
        return parseInt(index.substring(1));
    }
    return parseInt(index);
}

function parseMove(index, delta) {
    const sourceIndex = parseIndex(index);
    const [, destinationIndex] = delta;
    return [sourceIndex, destinationIndex];
}

function applyArrayDelta(obj, index, delta, version) {
    if (isSourceIndex(index)) {
        if (delta[0] === 3) {
            const [source, destination] = parseMove(index, delta);
            return obj.delete(source).insert(destination, obj.get(source));
        } else if (delta[0] === 0) {
            return obj.delete(parseIndex(index));
        } else {
            throw new Error('Undefined delta for source index' + delta);
        }
    } else {
        const intIndex = parseInt(index);
        const value = applyDelta(delta, obj.get(intIndex), version);
        if (!isUndefined(value)) {
            return obj.set(intIndex, value);
        } else {
            return obj.delete(intIndex);
        }
    }
}

function addIdentity(delta) {
    return Object.keys(delta).reduce((acc, key) => {
        if (!Array.isArray(acc[key])) {
            acc[key].__id = [1, key];
        }
        return acc;
    }, delta);
}

function identityArrayToMap(obj) {
    return (obj || Immutable.List()).reduce((acc, item) => {
        return acc.set(item.get('__id'), item);
    }, Immutable.Map());
}

function identityArrayIds(delta, obj, version, [key, meta]) {
    if (isUndefined(version) || meta.getIn(['identity', key], -1) < version) {
        return delta.ids;
    } else {
        return obj.map(v => v && v.get('__id'));
    }
}

function applyIdentityArray(delta, obj, version, ctx) {
    const versioned = !isUndefined(version);

    const key = ctx[0];
    const originalMap = identityArrayToMap(obj);
    const deltaMap = addIdentity(delta.map || {});
    const ids = identityArrayIds(delta, obj, version, ctx);

    let [meta, value] = ids.reduce(
        ([meta, acc], id) => {
            if (id === null) {
                return [meta, acc.push(null)];
            }

            if (!isUndefined(deltaMap[id])) {
                return [meta, acc.push(applyDelta(deltaMap[id], originalMap.get(id), version))];
            }

            return [meta, acc.push(originalMap.get(id))];
        },
        [ctx[1], Immutable.List()]
    );

    if (versioned) {
        meta = meta.updateIn(['identity', key], -1, old => Math.max(old, version));
    }
    return [[key, meta], value];
}

function applyArray(delta, obj, version) {
    // keys are sorted by command 0-delete, 1-change, 2-add
    const keys = Object.keys(delta)
        .filter(key => key !== '_t')
        .sort((a, b) => {
            if (delta[a][0] === delta[b][0]) {
                return parseIndex(b) - parseIndex(a);
            }
            return delta[a][0] - delta[b][0];
        });
    return keys.reduce((obj, index) => {
        return applyArrayDelta(obj, index, delta[index], version);
    }, obj || Immutable.List());
}

function toImmutableList(list) {
    return list ? Immutable.fromJS(list) : Immutable.List();
}

function applySet(delta, obj) {
    return (obj || Immutable.Set()).union(toImmutableList(delta['+'])).subtract(toImmutableList(delta['-']));
}

export function stripMeta(obj) {
    if (isPrimitive(obj) || obj === null || isUndefined(obj)) {
        return obj;
    } else if (Immutable.Map.isMap(obj)) {
        return obj
            .delete('__meta')
            .delete('__id')
            .map(stripMeta);
    } else if (Immutable.isImmutable(obj)) {
        return obj.map(stripMeta);
    }
    throw new Error("Don't know about " + obj);
}

export function markVersion(obj, version) {
    if (isPrimitive(obj) || obj === null || isUndefined(obj)) {
        return obj;
    } else if (Immutable.Map.isMap(obj)) {
        return obj
            .delete('__meta')
            .map(v => markVersion(v, version))
            .set(
                '__meta',
                Immutable.Map().set(
                    'version',
                    obj.filter(isPrimitive).map(_ => version)
                )
            );
    } else if (Immutable.isImmutable(obj)) {
        return obj.map(v => markVersion(v, version));
    }
    throw new Error("Don't know about " + obj);
}
